import axios from "axios";
import { PdfInitiator } from "../../../../utils/pdf/PdfInitiator";
import { QuotationPdfGenerator } from "./QuotationPdfGenerator";
import { getConfig } from "../../../../config/config";

function getPhotoDocumentBaseUrl() {
  const fromEnv =
    (typeof process !== "undefined" && process.env && (process.env.REACT_APP_PHOTO_DOCUMENT || process.env.PHOTO_DOCUMENT))
      ? String(process.env.REACT_APP_PHOTO_DOCUMENT || process.env.PHOTO_DOCUMENT).trim()
      : "";
  const fromConfig = String(getConfig?.("PHOTO_DOCUMENT", "") || "").trim();
  const fallbackEnv =
    (typeof process !== "undefined" && process.env && process.env.REACT_APP_FILE_URL)
      ? String(process.env.REACT_APP_FILE_URL).trim()
      : "";
  const fallbackConfig = String(getConfig?.("FILE_URL", "") || "").trim();
  const raw = fromEnv || fromConfig || fallbackEnv || fallbackConfig || "";
  try {
    if (typeof window !== "undefined" && window?.location?.protocol === "https:" && String(raw).startsWith("http://")) {
      return `https://${String(raw).slice("http://".length)}`;
    }
  } catch (_) {}
  return raw;
}

function normalizeUrlBase(baseUrl) {
  const s = String(baseUrl || "").trim();
  if (!s) return "";
  return s.endsWith("/") ? s : `${s}/`;
}

function trimSlashes(s) {
  return String(s || "").replace(/^\/+|\/+$/g, "");
}

function joinUrl(baseUrl, ...parts) {
  const base = normalizeUrlBase(baseUrl);
  if (!base) return "";
  const p = parts
    .map((v) => trimSlashes(v))
    .filter(Boolean)
    .join("/");
  return p ? `${base}${p}` : base;
}

function preferHttpsIfPageIsHttps(url) {
  const s = String(url || "").trim();
  if (!s) return "";
  try {
    if (typeof window !== "undefined" && window?.location?.protocol === "https:" && s.startsWith("http://")) {
      return `https://${s.slice("http://".length)}`;
    }
  } catch (_) {}
  return s;
}

function withPdfCacheBust(url) {
  const s = String(url || "").trim();
  if (!s) return "";
  // Avoid touching data URLs
  if (s.startsWith("data:image/")) return s;

  try {
    if (typeof window === "undefined") return s;
    const u = new URL(s, window.location.origin);
    // Only bust cache for PDF header/footer loads.
    u.searchParams.set("__pdfcb", String(Date.now()));
    // Preserve relative URLs if the original was relative.
    if (s.startsWith("/")) return `${u.pathname}${u.search}`;
    return u.toString();
  } catch (_) {
    return s;
  }
}

function rewriteToDevProxyIfLocalhost(url) {
  const s = String(url || "").trim();
  if (!s) return "";

  try {
    if (typeof window === "undefined") return s;
    const host = String(window?.location?.hostname || "").trim().toLowerCase();
    const isLocalhost = host === "localhost" || host === "127.0.0.1";
    if (!isLocalhost) return s;

    const u = new URL(s, window.location.origin);
    // Only rewrite the known production host/path we need to proxy during local dev.
    if (u.hostname === "online.ismartsales.com" && u.pathname.startsWith("/web/form/")) {
      return `${u.pathname}${u.search || ""}`;
    }
  } catch (_) {
    // ignore
  }
  return s;
}

async function tryLoadImage(url, { timeoutMs = 6000, crossOrigin } = {}) {
  const src = withPdfCacheBust(rewriteToDevProxyIfLocalhost(preferHttpsIfPageIsHttps(url)));
  if (!src) return false;
  if (typeof Image === "undefined") return false;

  return await new Promise((resolve) => {
    let done = false;
    const finish = (ok) => {
      if (done) return;
      done = true;
      resolve(!!ok);
    };

    const timer = setTimeout(() => finish(false), Math.max(500, Number(timeoutMs) || 6000));
    try {
      const img = new Image();
      // If crossOrigin is set and server does not return ACAO, many browsers fire onerror.
      // We use this to detect whether the image is embeddable into PDF via canvas/base64.
      if (crossOrigin !== undefined && crossOrigin !== null && crossOrigin !== "") {
        img.crossOrigin = crossOrigin;
      }
      try {
        img.referrerPolicy = "no-referrer";
      } catch (_) {}
      img.onload = () => {
        clearTimeout(timer);
        finish(true);
      };
      img.onerror = () => {
        clearTimeout(timer);
        finish(false);
      };
      img.src = src;
    } catch (_) {
      clearTimeout(timer);
      finish(false);
    }
  });
}

async function checkImageLoadability(url, { timeoutMs = 6000 } = {}) {
  const normalizedUrl = rewriteToDevProxyIfLocalhost(preferHttpsIfPageIsHttps(url));
  if (!normalizedUrl) return { url: "", exists: false, usable: false };

  // 1) Usable for embedding (CORS-safe)
  const usable = await tryLoadImage(normalizedUrl, { timeoutMs, crossOrigin: "anonymous" });
  if (usable) return { url: normalizedUrl, exists: true, usable: true };

  // 2) Exists (may open in browser) but not embeddable due to CORS/mixed-content rules
  const exists = await tryLoadImage(normalizedUrl, { timeoutMs, crossOrigin: null });
  return { url: normalizedUrl, exists: !!exists, usable: false };
}

// Backward-compatible helper (kept in case other code paths call it later)
async function canLoadImage(url, { timeoutMs = 6000 } = {}) {
  const st = await checkImageLoadability(url, { timeoutMs });
  return !!st.usable;
}

async function confirmProceedWithoutHeaderFooter({ company, missing, urls, statuses, translate, translator }) {
  const t = (token, fallback) =>
    typeof translator === "function" && token !== undefined && token !== null
      ? translator(token, fallback)
      : fallback;

  const escapeHtml = (raw) => {
    const s = String(raw ?? "");
    return s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;");
  };

  const prettyConfirm = ({ title, detailLines, confirmText, cancelText }) => {
    if (typeof document === "undefined" || !document?.body) {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      const isNarrow = (() => {
        try {
          return typeof window !== "undefined" && Number(window.innerWidth) > 0 && Number(window.innerWidth) < 520;
        } catch (_) {
          return false;
        }
      })();

      let done = false;
      const finish = (ok) => {
        if (done) return;
        done = true;
        try {
          document.removeEventListener("keydown", onKeyDown);
        } catch (_) {}
        try {
          overlay?.remove();
        } catch (_) {}
        resolve(!!ok);
      };

      const overlay = document.createElement("div");
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      overlay.style.cssText = [
        "position: fixed",
        "inset: 0",
        // Must be above SweetAlert2's .swal2-container (9999999999 !important in our styles)
        "z-index: 100000000000",
        "display: flex",
        "align-items: center",
        "justify-content: center",
        isNarrow ? "padding: 12px" : "padding: 24px",
        "background: rgba(15, 23, 42, 0.55)",
        // Avoid backdrop-filter here: it can cause hit-testing/click issues on some mobile browsers.
        "pointer-events: auto",
      ].join(";");

      const card = document.createElement("div");
      card.style.cssText = [
        "width: min(720px, 100%)",
        "background: #ffffff",
        "border-radius: 14px",
        "box-shadow: 0 18px 55px rgba(0,0,0,0.25)",
        "border: 1px solid rgba(15, 23, 42, 0.10)",
        "overflow: hidden",
        "position: relative",
        "pointer-events: auto",
        "font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial",
      ].join(";");

      const header = document.createElement("div");
      header.style.cssText = [
        "display: flex",
        "gap: 12px",
        "align-items: flex-start",
        "padding: 16px 18px",
        "background: linear-gradient(180deg, rgba(254, 243, 199, 0.9), rgba(255,255,255,1))",
        "border-bottom: 1px solid rgba(15, 23, 42, 0.08)",
      ].join(";");

      const icon = document.createElement("div");
      icon.style.cssText = [
        "width: 36px",
        "height: 36px",
        "border-radius: 10px",
        "background: #f59e0b",
        "display: flex",
        "align-items: center",
        "justify-content: center",
        "color: #111827",
        "font-weight: 900",
        "flex: 0 0 auto",
      ].join(";");
      icon.textContent = "!";

      const titleBox = document.createElement("div");
      titleBox.style.cssText = "min-width: 0;";

      const h = document.createElement("div");
      h.style.cssText = "font-size: 16px; font-weight: 800; color: #111827; line-height: 1.25;";
      h.textContent = String(title || "");

      const sub = document.createElement("div");
      sub.style.cssText = "margin-top: 2px; font-size: 13px; color: #6b7280; line-height: 1.4;";
      sub.textContent = t(null, "เอกสารจะไม่สมบูรณ์หากไม่มี Header/Footer");

      titleBox.appendChild(h);
      titleBox.appendChild(sub);
      header.appendChild(icon);
      header.appendChild(titleBox);

      const body = document.createElement("div");
      body.style.cssText = "padding: 14px 18px 6px; position: relative; z-index: 1;";

      const list = document.createElement("div");
      list.style.cssText = [
        "display: grid",
        "gap: 10px",
      ].join(";");

      const mkRow = ({ label, url, note }) => {
        const row = document.createElement("div");
        row.style.cssText = [
          "padding: 10px 12px",
          "border-radius: 12px",
          "border: 1px solid rgba(15, 23, 42, 0.08)",
          "background: #f8fafc",
        ].join(";");

        const top = document.createElement("div");
        top.style.cssText = "display:flex; align-items:center; justify-content:space-between; gap: 12px;";

        const left = document.createElement("div");
        left.style.cssText = "min-width:0;";

        const lbl = document.createElement("div");
        lbl.style.cssText = "font-weight: 700; color: #111827; font-size: 13px;";
        lbl.textContent = label;

        const u = document.createElement("div");
        u.style.cssText = "margin-top: 2px; color: #475569; font-size: 12px; word-break: break-all;";
        u.textContent = String(url || "");

        const n = document.createElement("div");
        n.style.cssText = "margin-top: 6px; color: #64748b; font-size: 12px; line-height: 1.35;";
        n.textContent = String(note || "");

        left.appendChild(lbl);
        left.appendChild(u);
        if (note) left.appendChild(n);

        const copyBtn = document.createElement("button");
        copyBtn.type = "button";
        copyBtn.textContent = t(null, "คัดลอกลิงก์");
        copyBtn.style.cssText = [
          "flex: 0 0 auto",
          "padding: 8px 10px",
          "border-radius: 10px",
          "border: 1px solid rgba(15, 23, 42, 0.12)",
          "background: #ffffff",
          "cursor: pointer",
          "font-size: 12px",
          "font-weight: 700",
          "color: #0f172a",
        ].join(";");
        copyBtn.onmouseenter = () => (copyBtn.style.background = "#f1f5f9");
        copyBtn.onmouseleave = () => (copyBtn.style.background = "#ffffff");
        copyBtn.onclick = async () => {
          try {
            const text = String(url || "");
            if (!text) return;
            if (navigator?.clipboard?.writeText) {
              await navigator.clipboard.writeText(text);
              copyBtn.textContent = t(null, "คัดลอกแล้ว");
              setTimeout(() => (copyBtn.textContent = t(null, "คัดลอกลิงก์")), 900);
              return;
            }
          } catch (_) {}
          try {
            const ta = document.createElement("textarea");
            ta.value = String(url || "");
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
            copyBtn.textContent = t(null, "คัดลอกแล้ว");
            setTimeout(() => (copyBtn.textContent = t(null, "คัดลอกลิงก์")), 900);
          } catch (_) {}
        };

        top.appendChild(left);
        top.appendChild(copyBtn);
        row.appendChild(top);
        return row;
      };

      (detailLines || []).forEach((d) => {
        list.appendChild(mkRow(d));
      });

      const hint = document.createElement("div");
      hint.style.cssText = "margin-top: 10px; font-size: 12px; color: #64748b;";
      hint.textContent = t(null, "ต้องการเปิดต่อ (ไม่มี Header/Footer) หรือยกเลิก?");

      body.appendChild(list);
      body.appendChild(hint);

      const footer = document.createElement("div");
      footer.style.cssText = [
        "display:flex",
        "justify-content:flex-end",
        "gap: 10px",
        "padding: 14px 18px 16px",
        "border-top: 1px solid rgba(15, 23, 42, 0.08)",
        "background: #ffffff",
        "position: relative",
        "z-index: 5",
        "pointer-events: auto",
        isNarrow ? "flex-wrap: wrap" : "",
      ].join(";");

      const cancel = document.createElement("button");
      cancel.type = "button";
      cancel.textContent = String(cancelText || "Cancel");
      cancel.style.cssText = [
        "padding: 12px 16px",
        "border-radius: 12px",
        "border: 1px solid rgba(15, 23, 42, 0.14)",
        "background: #ffffff",
        "cursor: pointer",
        "font-size: 13px",
        "font-weight: 800",
        "color: #0f172a",
        "min-height: 44px",
        "min-width: 120px",
        "pointer-events: auto",
        "user-select: none",
        "touch-action: manipulation",
        "-webkit-tap-highlight-color: transparent",
        isNarrow ? "width: 100%" : "",
      ].join(";");

      const onCancel = (e) => {
        try {
          e?.preventDefault?.();
          e?.stopPropagation?.();
        } catch (_) {}
        finish(false);
      };
      cancel.addEventListener("pointerup", onCancel);
      cancel.addEventListener("touchend", onCancel, { passive: false });
      cancel.addEventListener("click", onCancel);

      const ok = document.createElement("button");
      ok.type = "button";
      ok.textContent = String(confirmText || "OK");
      ok.style.cssText = [
        "padding: 12px 16px",
        "border-radius: 12px",
        "border: 0",
        "background: #0f172a",
        "cursor: pointer",
        "font-size: 13px",
        "font-weight: 800",
        "color: #ffffff",
        "min-height: 44px",
        "min-width: 160px",
        "pointer-events: auto",
        "user-select: none",
        "touch-action: manipulation",
        "-webkit-tap-highlight-color: transparent",
        isNarrow ? "width: 100%" : "",
      ].join(";");

      const onOk = (e) => {
        try {
          e?.preventDefault?.();
          e?.stopPropagation?.();
        } catch (_) {}
        finish(true);
      };
      ok.addEventListener("pointerup", onOk);
      ok.addEventListener("touchend", onOk, { passive: false });
      ok.addEventListener("click", onOk);

      // Make buttons feel responsive on hover/press
      cancel.onmouseenter = () => (cancel.style.background = "#f8fafc");
      cancel.onmouseleave = () => (cancel.style.background = "#ffffff");
      ok.onmouseenter = () => (ok.style.background = "#111827");
      ok.onmouseleave = () => (ok.style.background = "#0f172a");
      ok.onblur = () => (ok.style.transform = "scale(1)");

      footer.appendChild(cancel);
      footer.appendChild(ok);

      card.appendChild(header);
      card.appendChild(body);
      card.appendChild(footer);
      overlay.appendChild(card);

      const onKeyDown = (e) => {
        if (e?.key === "Escape") finish(false);
      };

      // Do NOT close on backdrop click/tap; it can be mis-triggered on some devices.
      document.addEventListener("keydown", onKeyDown);
      document.body.appendChild(overlay);

      // Initial focus (keyboard)
      try {
        ok.focus();
      } catch (_) {}
    });
  };

  const companyText = company ? ` (${company})` : "";
  const details = [];
  if (missing.includes("header")) {
    const st = statuses?.header;
    const url = preferHttpsIfPageIsHttps(String(st?.url || urls?.header || "").trim());
    const note = !st
      ? ""
      : !st.exists
        ? t(null, "ไม่พบไฟล์บนเซิร์ฟเวอร์ หรือ URL ไม่ถูกต้อง")
        : t(null, "พบไฟล์ แต่ไม่สามารถนำไปฝังใน PDF ได้ (CORS หรือ HTTP บนหน้า HTTPS)");
    details.push({
      label: `${t(null, "โหลด Header ไม่ได้")}${companyText}`,
      url,
      note,
    });
  }
  if (missing.includes("footer")) {
    const st = statuses?.footer;
    const url = preferHttpsIfPageIsHttps(String(st?.url || urls?.footer || "").trim());
    const note = !st
      ? ""
      : !st.exists
        ? t(null, "ไม่พบไฟล์บนเซิร์ฟเวอร์ หรือ URL ไม่ถูกต้อง")
        : t(null, "พบไฟล์ แต่ไม่สามารถนำไปฝังใน PDF ได้ (CORS หรือ HTTP บนหน้า HTTPS)");
    details.push({
      label: `${t(null, "โหลด Footer ไม่ได้")}${companyText}`,
      url,
      note,
    });
  }

  // Prefer pretty modal. If DOM is unavailable, fall back to window.confirm.
  try {
    return await prettyConfirm({
      title: t(translate?.ISA_Warn, "แจ้งเตือน"),
      detailLines: details,
      confirmText: t(null, "เปิดต่อ"),
      cancelText: t(null, "ยกเลิก"),
    });
  } catch (_) {
    // ignore
  }

  const lines = [];
  lines.push(t(translate?.ISA_Warn, "แจ้งเตือน") + ":");
  if (missing.includes("header")) {
    const st = statuses?.header;
    const url = preferHttpsIfPageIsHttps(String(st?.url || urls?.header || "").trim());
    const note = st ? (!st.exists ? " (ไม่พบไฟล์/URL ผิด)" : " (พบไฟล์แต่ฝัง PDF ไม่ได้: CORS/HTTP)") : "";
    lines.push(`- โหลด Header ไม่ได้${companyText}${note}: ${url}`.trim());
  }
  if (missing.includes("footer")) {
    const st = statuses?.footer;
    const url = preferHttpsIfPageIsHttps(String(st?.url || urls?.footer || "").trim());
    const note = st ? (!st.exists ? " (ไม่พบไฟล์/URL ผิด)" : " (พบไฟล์แต่ฝัง PDF ไม่ได้: CORS/HTTP)") : "";
    lines.push(`- โหลด Footer ไม่ได้${companyText}${note}: ${url}`.trim());
  }
  lines.push("");
  lines.push("ต้องการเปิด PDF ต่อ (โดยไม่มี Header/Footer) ใช่หรือไม่?");
  const msg = lines.filter(Boolean).join("\n");

  if (typeof window === "undefined" || typeof window.confirm !== "function") return false;
  return window.confirm(msg);
}

function openPdfInNewTabOrDownload(pdf, filename) {
  try {
    const blob = pdf.output("blob");
    const blobUrl = URL.createObjectURL(blob);

    const safeFilename = sanitizePdfFilename(filename);

    // Wrap the PDF blob in an HTML viewer.
    // Why: if we open the raw blob URL, the browser PDF viewer may download with a random GUID name.
    // This wrapper provides a real <a download> with the desired filename.
    const html = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${safeFilename.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</title>
    <style>
      html, body { height: 100%; margin: 0; }
      .bar { padding: 10px; font-family: Arial, sans-serif; border-bottom: 1px solid #eee; }
      .bar a { display: inline-block; padding: 8px 12px; text-decoration: none; border: 1px solid #333; border-radius: 6px; color: #111; }
      .bar small { margin-left: 10px; color: #666; }
      .frame { width: 100%; height: calc(100% - 52px); border: 0; }
    </style>
  </head>
  <body>
    <div class="bar">
      <a id="dl" href="${blobUrl}" download="${safeFilename.replace(/"/g, "&quot;")}">Download PDF</a>
      <small>${safeFilename.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</small>
    </div>
    <iframe class="frame" src="${blobUrl}"></iframe>
  </body>
</html>`;

    const htmlBlob = new Blob([html], { type: "text/html" });
    const htmlUrl = URL.createObjectURL(htmlBlob);

    // Prefer window.open; if blocked, fall back to an <a> click.
    const win = window.open(htmlUrl, "_blank", "noopener,noreferrer");
    if (!win) {
      const link = document.createElement("a");
      link.href = htmlUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    // Revoke later to keep the tab working.
    setTimeout(() => {
      try { URL.revokeObjectURL(htmlUrl); } catch (_) {}
      try { URL.revokeObjectURL(blobUrl); } catch (_) {}
    }, 60_000);
    return;
  } catch (e) {
    // Last resort: download
    try {
      pdf.save(filename);
    } catch (_) {}
  }
}

function sanitizePdfFilename(filename) {
  const safe = String(filename || "document.pdf")
    .replace(/[\\/\n\r\t\0]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return safe || "document.pdf";
}

function createPdfBlobUrl(pdf, filename) {
  // Some Android WebViews can mis-detect a Blob with an empty type.
  // Build via arraybuffer to enforce a real application/pdf MIME.
  let blob = null;
  try {
    const buf = pdf.output("arraybuffer");
    blob = new Blob([buf], { type: "application/pdf" });
  } catch (_) {
    try {
      const rawBlob = pdf.output("blob");
      blob = new Blob([rawBlob], { type: "application/pdf" });
    } catch (__) {
      blob = null;
    }
  }

  if (!blob) {
    // Fallback (should be rare)
    blob = pdf.output("blob");
  }

  const blobUrl = URL.createObjectURL(blob);
  return { blob, blobUrl, filename: sanitizePdfFilename(filename) };
}

export class QuotationPdfService {
  static dbConnectivityErrorMessage() {
    return "เกิดข้อผิดพลาดระหว่างการเชื่อมต่อกับฐานข้อมูล กรุณารอสักครู่แล้วลองทำรายการใหม่อีกครั้งนะครับ/ค่ะ";
  }

  static isDbConnectivityError(error) {
    try {
      const code = String(error?.code || "").toUpperCase();
      const message = String(error?.message || "").toLowerCase();

      // Axios timeout/network signals
      if (code === "ECONNABORTED" || code === "ETIMEDOUT") return true;
      if (message.includes("timeout") || message.includes("network error")) return true;
      if (code === "ECONNRESET" || code === "EPIPE" || code === "ENETUNREACH") return true;

      const status = Number(error?.response?.status || 0);
      // Gateway / service unavailable often surfaces during DB overload or deadlocks.
      if ([502, 503, 504].includes(status)) return true;

      // Detect SQL Server deadlock/lock timeout patterns (server-side message)
      const data = error?.response?.data;
      const serverText = String(
        (typeof data === "string" ? data : "") ||
          data?.message ||
          data?.msg ||
          data?.error ||
          (Array.isArray(data?.errors) && (data.errors[0]?.message || data.errors[0]?.msg)) ||
          ""
      ).toLowerCase();

      if (!serverText) return false;
      if (serverText.includes("deadlock")) return true;
      if (serverText.includes("was deadlocked on lock resources")) return true;
      if (serverText.includes("lock request time out period exceeded")) return true;
      if (serverText.includes("sqlstate") && serverText.includes("40001")) return true;
      return false;
    } catch (_) {
      return false;
    }
  }

  static formatError(error, context = "") {
    try {
      if (QuotationPdfService.isDbConnectivityError(error)) {
        return QuotationPdfService.dbConnectivityErrorMessage();
      }

      // Axios-style error
      const status = error?.response?.status;
      const statusText = error?.response?.statusText;
      const url = error?.config?.url;

      const data = error?.response?.data;
      const serverMsg =
        (typeof data === "string" ? data : null) ||
        data?.message ||
        data?.msg ||
        data?.error ||
        (Array.isArray(data?.errors) && data.errors[0]?.message) ||
        (Array.isArray(data?.errors) && data.errors[0]?.msg) ||
        null;

      const baseMsg = error?.message || "เกิดข้อผิดพลาด";

      const parts = [];
      if (context) parts.push(context);
      parts.push(baseMsg);
      if (status) parts.push(`HTTP ${status}${statusText ? ` ${statusText}` : ""}`);
      if (url) parts.push(`URL: ${url}`);
      if (serverMsg) parts.push(`Server: ${String(serverMsg).slice(0, 300)}`);

      return parts.join(" | ");
    } catch (_) {
      return error?.message || "เกิดข้อผิดพลาด";
    }
  }

  static buildAuthHeaders(options = {}) {
    const tokenFromOptionsRaw = String(options?.authorizationToken || "").trim();
    const tokenFromLocalStorage = (() => {
      try {
        return String(localStorage.getItem("x-access-token") || "").trim();
      } catch (_) {
        return "";
      }
    })();

    const normalizeToken = (t) => {
      const s = String(t || "").trim();
      if (!s) return "";
      let out = s.replace(/^Bearer\s+/i, "").trim();
      // Allow common wrapper mistakes: <token> or "token" or 'token'
      if (
        (out.startsWith("<") && out.endsWith(">")) ||
        (out.startsWith("\"") && out.endsWith("\"")) ||
        (out.startsWith("'") && out.endsWith("'"))
      ) {
        out = out.slice(1, -1).trim();
      }
      return out;
    };

    const token = normalizeToken(tokenFromOptionsRaw) || normalizeToken(tokenFromLocalStorage);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  static async fetchAndGeneratePdf(quotationData, options = {}) {
    try {
      const pdfInitiator = new PdfInitiator();
      const fontReadyPromise = pdfInitiator.initAsync();

      const timeoutMs = Number(options?.timeoutMs ?? 45_000);

      const {
        pdfType = "with_date",
        docHeaderTypeCode,
        docHeaderImageFilename: docHeaderImageFilenameFromCaller,
        docHeaderImageFileLocation: docHeaderImageFileLocationFromCaller,
        translate,
        translator,
      } = options;

      // Always log for troubleshooting.

      // ใช้ endpoint ที่มีอยู่จริงในระบบ (เหมือนหน้าฟอร์มแก้ไขเอกสาร)
      const withDetailUrl = `${process.env.REACT_APP_API_URL}quotationTransaction/withDetail`;
      const findByIdUrl = `${process.env.REACT_APP_API_URL}quotationTransaction/findById`;
      const extraConditionsUrl = `${process.env.REACT_APP_API_URL}quotationTransaction/extraConditions`;
      const transactionItemUrl = `${process.env.REACT_APP_API_URL}quotationTransaction/transactionItem`;

      const language = options.language || 1;
      const salesmanCode = options.salesmanCode || quotationData?.SalesmanCode || "";
      const revision = quotationData?.Revision ?? quotationData?.revision ?? null;

      const headers = QuotationPdfService.buildAuthHeaders(options);

      const normalizeArray = (val) => {
        if (Array.isArray(val)) return val;
        if (val && typeof val === "object") {
          if (Array.isArray(val.rows)) return val.rows;
          if (Array.isArray(val.data)) return val.data;
        }
        return [];
      };

      const fetchExtraConditions = async ({ filename } = {}) => {
        try {
          const safeFilename = String(filename || "").trim();
          const params = {
            docNumber: quotationData?.DocNumber,
            company: quotationData?.Company,
            // New API: expects `langId` + `filename`
            langId: language,
          };
          if (safeFilename) params.filename = safeFilename;
          if (revision !== null && revision !== undefined) params.revision = revision;

          const res = await axios.get(extraConditionsUrl, { params, headers, timeout: timeoutMs });
          try {
            // eslint-disable-next-line no-console
            console.log("[QuotationPdfService] extraConditions response", {
              url: extraConditionsUrl,
              params,
              response: res?.data,
            });
          } catch (_) {}

          if (res?.data?.success === false) {
            try {
              // eslint-disable-next-line no-console
              console.warn("[QuotationPdfService] extraConditions returned success:false", {
                url: extraConditionsUrl,
                params,
                hasAuthHeader: !!headers?.Authorization,
                errors: res?.data?.errors,
              });
            } catch (_) {}
            return [];
          }

          const payload = res?.data?.data ?? res?.data ?? null;

          // New API: returns rows directly with fields
          // SeqNo,Condtype,labelkey,labelvalue,value,lookupdescription
          // Old API: may return { Conditions: [...] }
          const rows =
            (Array.isArray(payload) && payload) ||
            payload?.data ||
            payload?.rows ||
            payload?.Conditions ||
            payload?.conditions ||
            payload?.data?.Conditions ||
            payload?.data?.conditions ||
            [];

          const normalized = normalizeArray(rows);
          try {
            // eslint-disable-next-line no-console
            console.log("[QuotationPdfService] extraConditions normalized", {
              count: normalized?.length ?? 0,
              Conditions: normalized,
            });
          } catch (_) {}
          return normalized;
        } catch (e) {
          try {
            // eslint-disable-next-line no-console
            console.warn("[QuotationPdfService] extraConditions request failed", {
              url: extraConditionsUrl,
              params: {
                company: quotationData?.Company,
                docNumber: quotationData?.DocNumber,
                filename: String(filename || "").trim() || undefined,
                langId: language,
                language,
                salesmanCode,
              },
              hasAuthHeader: !!headers?.Authorization,
              message: QuotationPdfService.formatError(e, "extraConditions"),
              status: e?.response?.status,
              response: e?.response?.data,
            });
          } catch (_) {}
          return [];
        }
      };

      const fetchTransactionItems = async () => {
        try {
          const params = {
            company: quotationData?.Company,
            docNumber: quotationData?.DocNumber,
            language,
          };
          if (revision !== null && revision !== undefined) params.revision = revision;

          const res = await axios.get(transactionItemUrl, { params, headers, timeout: timeoutMs });

          // Support shapes:
          // - { success, data: [...] }
          // - { data: [...] }
          // - [...]
          const payload = res?.data?.data ?? res?.data ?? null;
          if (payload?.success === false) return [];

          const rows =
            (Array.isArray(payload) && payload) ||
            payload?.data ||
            payload?.rows ||
            payload?.QuotationTransactionItem ||
            payload?.QuotationTransactionItems ||
            [];
          console.log("item response", { rows, payload });
          return normalizeArray(rows);
          
        } catch (e) {
          try {
            // eslint-disable-next-line no-console
            console.warn("[QuotationPdfService] transactionItem request failed", {
              url: transactionItemUrl,
              params: {
                company: quotationData?.Company,
                docNumber: quotationData?.DocNumber,
                language,
              },
              hasAuthHeader: !!headers?.Authorization,
              message: QuotationPdfService.formatError(e, "transactionItem"),
              status: e?.response?.status,
            });
          } catch (_) {}
          return [];
        }
      };

      const normalizeKeyLoose = (key) => String(key ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const getValueLoose = (obj, key) => {
        if (!obj || typeof obj !== "object") return undefined;
        const direct = obj[key];
        if (direct !== undefined) return direct;
        const target = normalizeKeyLoose(key);
        for (const k of Object.keys(obj)) {
          if (normalizeKeyLoose(k) === target) return obj[k];
        }
        return undefined;
      };

      // Resolve header image mapping via API when caller didn't provide ImageFilename.
      // GET /quotationTransaction/docHeaderImage?company=..&lookupcode=..
      let docHeaderImageFilename = String(docHeaderImageFilenameFromCaller || "").trim();
      let docHeaderImageFileLocation = String(docHeaderImageFileLocationFromCaller || "").trim();
      if (!docHeaderImageFilename && docHeaderTypeCode && quotationData?.Company) {
        try {
          const url = `${process.env.REACT_APP_API_URL}quotationTransaction/docHeaderImage`;
          const normalizeToArray = (val, visited = new WeakSet()) => {
            if (Array.isArray(val)) return val;
            if (!val || typeof val !== "object") return [];
            if (visited.has(val)) return [];
            visited.add(val);

            for (const k of Object.keys(val)) {
              const child = val[k];
              if (Array.isArray(child)) return child;
            }
            for (const k of Object.keys(val)) {
              const child = val[k];
              if (child && typeof child === "object") {
                const arr = normalizeToArray(child, visited);
                if (arr.length) return arr;
              }
            }
            return [];
          };

          const resolveFromResponse = (res) => {
            const raw = res?.data?.data ?? res?.data ?? null;
            const rows = normalizeToArray(raw);
            const obj = rows.length ? rows[0] : (Array.isArray(raw) ? raw[0] : raw);
            return {
              filename: String(
                getValueLoose(obj, "ImageFilename") ?? getValueLoose(obj, "ImageFileName") ?? ""
              ).trim(),
              location: String(getValueLoose(obj, "FileLocation") ?? "").trim(),
            };
          };

          const res = await axios.get(url, {
            params: {
              company: quotationData.Company,
              docHeaderType: docHeaderTypeCode,
              // keep legacy params for backward compatibility
              lookupcode: docHeaderTypeCode,
              lookupCode: docHeaderTypeCode,
            },
            headers,
            timeout: timeoutMs,
          });
          const { filename, location } = resolveFromResponse(res);
          docHeaderImageFilename = filename;
          docHeaderImageFileLocation = location;

          if (!docHeaderImageFilename) {
            try {
              // eslint-disable-next-line no-console
              console.warn("[QuotationPdfService] docHeaderImage returned empty mapping", {
                url,
                params: {
                  company: quotationData.Company,
                  docHeaderType: docHeaderTypeCode,
                  lookupcode: docHeaderTypeCode,
                },
                hasAuthHeader: !!headers?.Authorization,
                response: res?.data,
              });
            } catch (_) {}
          }

          // Retry with alternate param name if request succeeded but returned no filename.
          if (!docHeaderImageFilename) {
            try {
              const res2 = await axios.get(url, {
                params: {
                  company: quotationData.Company,
                  docHeaderType: docHeaderTypeCode,
                  lookupcode: docHeaderTypeCode,
                  lookupCode: docHeaderTypeCode,
                },
                headers,
                timeout: timeoutMs,
              });
              const resolved2 = resolveFromResponse(res2);
              docHeaderImageFilename = resolved2.filename || docHeaderImageFilename;
              docHeaderImageFileLocation = resolved2.location || docHeaderImageFileLocation;
            } catch (_) {
              // ignore retry
            }
          }
        } catch (_) {
          try {
            // eslint-disable-next-line no-console
            console.warn("[QuotationPdfService] docHeaderImage request failed (fetchAndGeneratePdf)", {
              company: quotationData?.Company,
              docHeaderType: docHeaderTypeCode,
              hasAuthHeader: !!headers?.Authorization,
              message: QuotationPdfService.formatError(_, "docHeaderImage"),
            });
          } catch (__) {}
          // fall back to default header
        }
      }

      let apiData = null;
      try {
        const params = {
          docNumber: quotationData.DocNumber,
          company: quotationData.Company,
          language,
        };
        // Some environments may require salesmanCode for permission/visibility.
        // Only send when we actually have a value.
        if (salesmanCode) params.salesmanCode = salesmanCode;
        if (revision !== null && revision !== undefined) params.revision = revision;

        const response = await axios.get(withDetailUrl, {
          params,
          headers,
          timeout: timeoutMs,
        });

        apiData = response.data?.data || null;
      } catch (err) {
        // fallback: some envs may not have withDetail (404) or may gate it by permission (401/403)
        const status = err?.response?.status;
        if (status === 404 || status === 401 || status === 403) {
          try {
            const response = await axios.get(findByIdUrl, {
              params: {
                docNumber: quotationData.DocNumber,
                company: quotationData.Company,
                language,
                ...(revision !== null && revision !== undefined ? { revision } : {}),
              },
              headers,
              timeout: timeoutMs,
            });
            apiData = response.data?.data || response.data;
          } catch (err2) {
            if (QuotationPdfService.isDbConnectivityError(err) || QuotationPdfService.isDbConnectivityError(err2)) {
              throw new Error(QuotationPdfService.dbConnectivityErrorMessage());
            }
            const ctx = status === 404
              ? "โหลดข้อมูลเอกสาร (withDetail ไม่พบ)"
              : "โหลดข้อมูลเอกสาร (withDetail ไม่มีสิทธิ์)";
            throw new Error(
              `${QuotationPdfService.formatError(err, ctx)} | fallback(findById): ${QuotationPdfService.formatError(err2)}`
            );
          }
        } else {
          throw new Error(QuotationPdfService.formatError(err, "โหลดข้อมูลเอกสาร (withDetail) ไม่สำเร็จ"));
        }
      }

      if (!apiData) {
        throw new Error("ไม่พบข้อมูลเอกสารสำหรับสร้าง PDF");
      }

      // Normalize header early (needed for extraConditions new API: requires QuotationTransaction.Filename)
      const headerCandidateEarly = apiData.QuotationTransaction || apiData;
      const headerObjEarly = Array.isArray(headerCandidateEarly) ? headerCandidateEarly[0] : headerCandidateEarly;
      const headerEarly =
        headerObjEarly && typeof headerObjEarly === "object"
          ? { ...headerObjEarly }
          : {};

      const qtFilename = String(
        quotationData?.Filename ??
          quotationData?.filename ??
          getValueLoose(headerEarly, "Filename") ??
          getValueLoose(apiData, "Filename") ??
          ""
      ).trim();

      // Some environments return ProductRemarks/ProductRemarksFooter only on other endpoints.
      // If withDetail did not include them, try fetching from findById and merge.
      const extraConditions = qtFilename
        ? await fetchExtraConditions({ filename: qtFilename })
        : [];
      if (!qtFilename) {
        try {
          // eslint-disable-next-line no-console
          console.warn("[QuotationPdfService] extraConditions skipped: missing QuotationTransaction.Filename", {
            DocNumber: quotationData?.DocNumber,
            Company: quotationData?.Company,
            headerFilename: getValueLoose(headerEarly, "Filename"),
          });
        } catch (_) {}
      }

      const footerFromApi =
        apiData.ProductRemarksFooter ??
        apiData.productRemarksFooter ??
        apiData.ProductRemarkFooter ??
        apiData.productRemarkFooter ??
        apiData.ProductRemarksFooters ??
        apiData.productRemarksFooters;

      const remarksFromApi =
        apiData.ProductRemarks ??
        apiData.productRemarks ??
        apiData.ProductRemark ??
        apiData.productRemark;

      // Fetch transaction items from the new endpoint (preferred) to get Unit1/Quantity1/Unit2/Quantity2.
      // Fall back to the existing withDetail/findById payload if the new endpoint returns empty.
      const transactionItemsFromApi = await fetchTransactionItems();

      // Normalize from whichever key the API used, so downstream always reads the same fields.
      // (Some envs return ProductRemarkFooter/ProductRemarksFooters instead of ProductRemarksFooter.)
      const footerSourceKey =
        apiData.ProductRemarksFooter !== undefined ? "ProductRemarksFooter" :
        apiData.productRemarksFooter !== undefined ? "productRemarksFooter" :
        apiData.ProductRemarkFooter !== undefined ? "ProductRemarkFooter" :
        apiData.productRemarkFooter !== undefined ? "productRemarkFooter" :
        apiData.ProductRemarksFooters !== undefined ? "ProductRemarksFooters" :
        apiData.productRemarksFooters !== undefined ? "productRemarksFooters" :
        null;

      const remarksSourceKey =
        apiData.ProductRemarks !== undefined ? "ProductRemarks" :
        apiData.productRemarks !== undefined ? "productRemarks" :
        apiData.ProductRemark !== undefined ? "ProductRemark" :
        apiData.productRemark !== undefined ? "productRemark" :
        null;

      const normalizedFooterFromApi = normalizeArray(footerFromApi);
      const normalizedRemarksFromApi = normalizeArray(remarksFromApi);

      const hasFooterRemarks = normalizedFooterFromApi.length > 0;
      const hasRemarks = normalizedRemarksFromApi.length > 0;

      try {
        const sample = normalizedFooterFromApi.slice(0, 8).map((r) => {
          const keys = r && typeof r === "object" ? Object.keys(r).slice(0, 25) : [];
          const productCode = r?.ProductCode ?? r?.productCode ?? r?.ItemCode ?? r?.itemCode;
          const lineNo = r?.LineNo ?? r?.lineNo ?? r?.Seq ?? r?.seq ?? r?.Line ?? r?.line;
          const subject = r?.Subject ?? r?.subject ?? r?.Title ?? r?.title;
          const remarks = r?.Remarks ?? r?.remarks ?? r?.Remark ?? r?.remark ?? r?.Description ?? r?.description;
          const remarksText = String(remarks ?? "");
          return {
            keys,
            productCode,
            lineNo,
            subject,
            remarksLen: remarksText.length,
            remarksPreview: remarksText.slice(0, 160),
          };
        });

        // eslint-disable-next-line no-console
        console.log("[QuotationPdfService] ProductRemarksFooter payload", {
          DocNumber: quotationData?.DocNumber,
          Company: quotationData?.Company,
          footerSourceKey,
          remarksSourceKey,
          footerRawType: footerFromApi === null ? "null" : Array.isArray(footerFromApi) ? "array" : typeof footerFromApi,
          footerNormalizedCount: normalizedFooterFromApi.length,
          remarksNormalizedCount: normalizedRemarksFromApi.length,
          footerSample: sample,
        });
      } catch (_) {}

      // Ensure consistent fields for generator and debug.
      apiData.ProductRemarksFooter = normalizedFooterFromApi;
      apiData.ProductRemarks = normalizedRemarksFromApi;

      if (!hasFooterRemarks || !hasRemarks) {
        try {
          const response2 = await axios.get(findByIdUrl, {
            params: {
              docNumber: quotationData.DocNumber,
              company: quotationData.Company,
              language,
            },
            headers,
            timeout: timeoutMs,
          });
          const fallbackData = response2.data?.data || response2.data;

          if (fallbackData) {
            const footerFromFallback =
              fallbackData.ProductRemarksFooter ??
              fallbackData.productRemarksFooter ??
              fallbackData.ProductRemarkFooter ??
              fallbackData.productRemarkFooter ??
              fallbackData.ProductRemarksFooters ??
              fallbackData.productRemarksFooters;

            const remarksFromFallback =
              fallbackData.ProductRemarks ??
              fallbackData.productRemarks ??
              fallbackData.ProductRemark ??
              fallbackData.productRemark;

            if (!hasFooterRemarks) {
              apiData.ProductRemarksFooter = normalizeArray(footerFromFallback);
            }
            if (!hasRemarks) {
              apiData.ProductRemarks = normalizeArray(remarksFromFallback);
            }
          }
        } catch (_) {
          // ignore fallback failure
        }
      }

      // Final normalize (in case fallback filled it)
      apiData.ProductRemarksFooter = normalizeArray(apiData.ProductRemarksFooter);
      apiData.ProductRemarks = normalizeArray(apiData.ProductRemarks);

      try {
        // eslint-disable-next-line no-console
        console.log("[QuotationPdfService] ProductRemarksFooter normalized (final)", {
          DocNumber: quotationData?.DocNumber,
          Company: quotationData?.Company,
          count: apiData.ProductRemarksFooter.length,
          sample: apiData.ProductRemarksFooter.slice(0, 8),
        });
      } catch (_) {}

      // Normalize shape ให้ generator ใช้งานได้
      const headerCandidate = apiData.QuotationTransaction || apiData;
      const headerObj = Array.isArray(headerCandidate) ? headerCandidate[0] : headerCandidate;
      const header =
        headerObj && typeof headerObj === "object"
          ? { ...headerObj }
          : {};

      // Some endpoints return transaction columns at the root object (SELECT * result)
      // while also including nested QuotationTransaction/QuotationTransactionItem.
      // Ensure totals columns exist on the header object so the PDF reads them directly.
      const copyIfMissing = (key) => {
        if (header[key] !== undefined && header[key] !== null && header[key] !== "") return;
        const v = apiData?.[key];
        if (v === undefined || v === null || v === "") return;
        header[key] = v;
      };
      [
        "Total_Sum",
        "Total_sum",
        "Total_Vat",
        "Total_vat",
        "Total_GrandTotal",
        "Total_Grandtotal",
        // keep around in case some env uses these columns
        "Total_Total",
        "Total_Discount",
      ].forEach(copyIfMissing);

      // If some list-only fields are missing from withDetail/findById,
      // fall back to the row payload passed from QuotationTransaction list (row.original).
      const copyIfMissingFromInput = (key) => {
        if (header[key] !== undefined && header[key] !== null && header[key] !== "") return;
        const v = quotationData?.[key];
        if (v === undefined || v === null || v === "") return;
        header[key] = v;
      };
      [
        "QtStatusName",
        "DocStatusName",
        "DocStatus",
      ].forEach(copyIfMissingFromInput);
      const customer = apiData.Customer || apiData.customer || null;
      const contact = apiData.Contact || apiData.contact || null;

      const documentData = {
        DocNumber: header?.DocNumber || quotationData.DocNumber,
        Company: header?.Company || quotationData.Company,
        QuotationTransaction: header,
        Customer: customer,
        Contact: contact,
        QuotationTransactionItem:
          transactionItemsFromApi.length
            ? transactionItemsFromApi
            : apiData.QuotationTransactionItem || headerCandidate?.QuotationTransactionItem || [],
        QuotationTransactionDiscount:
          apiData.QuotationTransactionDiscount || apiData.OrderTransactionDiscount || [],
        ProductRemarks: apiData.ProductRemarks || [],
        ProductRemarksFooter: apiData.ProductRemarksFooter || [],
        ExtraConditions: extraConditions,
        TransactionStatus: apiData.TransactionStatus || null,
      };

      try {
        // eslint-disable-next-line no-console
        console.log("[QuotationPdfService] documentData for PDF", documentData);
      } catch (_) {}

      try {
        // eslint-disable-next-line no-console
        console.log("[QuotationPdfService] Totals debug (normalized header)", {
          DocNumber: documentData?.DocNumber,
          Company: documentData?.Company,
          header: {
            Total_Sum: header?.Total_Sum,
            Total_sum: header?.Total_sum,
            Total_Vat: header?.Total_Vat,
            Total_vat: header?.Total_vat,
            Total_GrandTotal: header?.Total_GrandTotal,
            Total_Grandtotal: header?.Total_Grandtotal,
            // for comparison only
            Total_Total: header?.Total_Total,
          },
          root: {
            Total_Sum: apiData?.Total_Sum,
            Total_sum: apiData?.Total_sum,
            Total_Vat: apiData?.Total_Vat,
            Total_vat: apiData?.Total_vat,
            Total_GrandTotal: apiData?.Total_GrandTotal,
            Total_Grandtotal: apiData?.Total_Grandtotal,
            Total_Total: apiData?.Total_Total,
          },
        });
      } catch (_) {}

      // ปรับแต่งข้อมูลตาม pdfType
      if (pdfType === 'without_date') {
        // ถ้าเลือกแบบไม่ระบุวันที่ ให้ลบวันที่ออก
        if (documentData.QuotationTransaction) {
          documentData.QuotationTransaction.DocDate = null;
          documentData.QuotationTransaction.DueDate = null;
          documentData.QuotationTransaction.ValidDate = null;
        }
      }

      // สร้าง PDF
      await fontReadyPromise;

      // Strict company header/footer from ENV only.
      // Require: `${Company}_header_quotation.png` and `${Company}_footer_quotation.png` under PHOTO_DOCUMENT base URL
      const strictCompanyHeaderFooter = true;
      const company = String(documentData?.Company || "").trim();
      const fileBaseUrl = getPhotoDocumentBaseUrl();
      const location = "";
      const baseEndsWithLocation = (baseUrl, loc) => {
        const base = String(baseUrl || "").trim().replace(/\/+$/, "");
        const locationText = String(loc || "").trim().replace(/^\/+|\/+$/g, "");
        if (!base || !locationText) return false;
        return base.toLowerCase().endsWith(`/${locationText.toLowerCase()}`);
      };

      const hasDynamicHeaderSelection = !!(
        String(docHeaderTypeCode || "").trim() ||
        String(docHeaderImageFilename || "").trim() ||
        String(docHeaderImageFileLocation || "").trim()
      );

      const headerUrlSelected = (() => {
        const fn = String(docHeaderImageFilename || "").trim();
        if (!fn) return "";
        const loc = String(docHeaderImageFileLocation || "").trim();
        const effectiveLoc = baseEndsWithLocation(fileBaseUrl, loc) ? "" : loc;
        return joinUrl(fileBaseUrl, effectiveLoc, fn);
      })();

      const headerUrlCompany = company
        ? joinUrl(fileBaseUrl, location, `${company}_header_quotation.png`)
        : "";

      const headerUrl = (hasDynamicHeaderSelection && headerUrlSelected) ? headerUrlSelected : headerUrlCompany;
      const footerUrl = company
        ? joinUrl(fileBaseUrl, location, `${company}_footer_quotation.png`)
        : "";

      let allowMissingCompanyHeaderFooter = false;
      const missing = [];
      let headerStatus = { url: preferHttpsIfPageIsHttps(headerUrl), exists: false, usable: false };
      let footerStatus = { url: preferHttpsIfPageIsHttps(footerUrl), exists: false, usable: false };
      if (!fileBaseUrl || !company) {
        missing.push("header", "footer");
      } else {
        const [h, f] = await Promise.all([
          checkImageLoadability(headerUrl, { timeoutMs }),
          checkImageLoadability(footerUrl, { timeoutMs }),
        ]);
        headerStatus = h;
        footerStatus = f;
        if (!h.usable) missing.push("header");
        if (!f.usable) missing.push("footer");
      }

      if (missing.length) {
        const proceed = await confirmProceedWithoutHeaderFooter({
          company,
          missing,
          urls: { header: headerStatus.url, footer: footerStatus.url },
          statuses: { header: headerStatus, footer: footerStatus },
          translate,
          translator,
        });
        if (!proceed) {
          throw new Error("ยกเลิกการเปิด PDF เนื่องจากไม่พบ Header/Footer ของบริษัท");
        }
        allowMissingCompanyHeaderFooter = true;
      }

      const generator = new QuotationPdfGenerator(documentData, {
        summaryOnEveryPage: true,
        docHeaderTypeCode,
        docHeaderImageFilename,
        docHeaderImageFileLocation,
        strictCompanyHeaderFooter,
        allowMissingCompanyHeaderFooter,
        companyHeaderFooterLocation: location,
        // Generator also logs totals once per PDF.
        debugTotals: true,
        translate,
        translator,
      });

      let pdf = null;
      try {
        pdf = await generator.generate();
      } catch (e) {
        const msg = String(e?.message || "");
        const isHeaderErr = msg.includes("PDF_HEADER_NOT_FOUND_OR_UNUSABLE");
        const isFooterErr = msg.includes("PDF_FOOTER_NOT_FOUND_OR_UNUSABLE");

        if (strictCompanyHeaderFooter && !allowMissingCompanyHeaderFooter && (isHeaderErr || isFooterErr)) {
          const missing2 = [];
          if (isHeaderErr) missing2.push("header");
          if (isFooterErr) missing2.push("footer");

          const proceed = await confirmProceedWithoutHeaderFooter({
            company,
            missing: missing2.length ? missing2 : ["header", "footer"],
            urls: { header: headerStatus.url, footer: footerStatus.url },
            statuses: { header: headerStatus, footer: footerStatus },
            translate,
            translator,
          });
          if (!proceed) throw e;

          const generator2 = new QuotationPdfGenerator(documentData, {
            summaryOnEveryPage: true,
            docHeaderTypeCode,
            docHeaderImageFilename,
            docHeaderImageFileLocation,
            strictCompanyHeaderFooter,
            allowMissingCompanyHeaderFooter: true,
            companyHeaderFooterLocation: location,
            debugTotals: true,
            translate,
            translator,
          });
          pdf = await generator2.generate();
        } else {
          throw e;
        }
      }

      const filename = `Quotation-${quotationData.DocNumber}.pdf`;
      const output = String(options?.output || "").toLowerCase();
      if (output === "bloburl" || output === "inline") {
        const { blob, blobUrl, filename: safeFilename } = createPdfBlobUrl(pdf, filename);
        return { success: true, blob, blobUrl, filename: safeFilename };
      }

      openPdfInNewTabOrDownload(pdf, filename);
      return { success: true };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[QuotationPdfService] fetchAndGeneratePdf failed", error);
      const msg = QuotationPdfService.formatError(error, "สร้าง PDF ไม่สำเร็จ");
      throw new Error(msg);
    }
  }

  static async downloadPdf(quotationData, options = {}) {
    try {
      const pdfInitiator = new PdfInitiator();
      const fontReadyPromise = pdfInitiator.initAsync();

      const timeoutMs = Number(options?.timeoutMs ?? 30_000);

      const {
        pdfType = "with_date",
        docHeaderTypeCode,
        docHeaderImageFilename: docHeaderImageFilenameFromCaller,
        docHeaderImageFileLocation: docHeaderImageFileLocationFromCaller,
        translate,
        translator,
      } = options;

      // Always log for troubleshooting.

      const withDetailUrl = `${process.env.REACT_APP_API_URL}quotationTransaction/withDetail`;
      const findByIdUrl = `${process.env.REACT_APP_API_URL}quotationTransaction/findById`;
      const extraConditionsUrl = `${process.env.REACT_APP_API_URL}quotationTransaction/extraConditions`;
      const transactionItemUrl = `${process.env.REACT_APP_API_URL}quotationTransaction/transactionItem`;

      const language = options.language || 1;
      const salesmanCode = options.salesmanCode || quotationData?.SalesmanCode || "";
      const revision = quotationData?.Revision ?? quotationData?.revision ?? null;

      const headers = QuotationPdfService.buildAuthHeaders(options);

      const normalizeArray = (val) => {
        if (Array.isArray(val)) return val;
        if (val && typeof val === "object") {
          if (Array.isArray(val.rows)) return val.rows;
          if (Array.isArray(val.data)) return val.data;
        }
        return [];
      };

      const fetchExtraConditions = async ({ filename } = {}) => {
        try {
          const safeFilename = String(filename || "").trim();
          const params = {
            docNumber: quotationData?.DocNumber,
            company: quotationData?.Company,
            // New API: expects `langId` + `filename`
            langId: language,
          };
          if (safeFilename) params.filename = safeFilename;
          if (revision !== null && revision !== undefined) params.revision = revision;
          // Do not send legacy params (language/salesmanCode) to avoid triggering old query paths.

          const res = await axios.get(extraConditionsUrl, { params, headers, timeout: timeoutMs });
          try {
            // eslint-disable-next-line no-console
            console.log("[QuotationPdfService] extraConditions response", {
              url: extraConditionsUrl,
              params,
              response: res?.data,
            });
          } catch (_) {}

          if (res?.data?.success === false) {
            try {
              // eslint-disable-next-line no-console
              console.warn("[QuotationPdfService] extraConditions returned success:false", {
                url: extraConditionsUrl,
                params,
                hasAuthHeader: !!headers?.Authorization,
                errors: res?.data?.errors,
              });
            } catch (_) {}
            return [];
          }

          const payload = res?.data?.data ?? res?.data ?? null;

          const rows =
            (Array.isArray(payload) && payload) ||
            payload?.data ||
            payload?.rows ||
            payload?.Conditions ||
            payload?.conditions ||
            payload?.data?.Conditions ||
            payload?.data?.conditions ||
            [];

          const normalized = normalizeArray(rows);
          try {
            // eslint-disable-next-line no-console
            console.log("[QuotationPdfService] extraConditions normalized", {
              count: normalized?.length ?? 0,
              Conditions: normalized,
            });
          } catch (_) {}
          return normalized;
        } catch (e) {
          try {
            // eslint-disable-next-line no-console
            console.warn("[QuotationPdfService] extraConditions request failed", {
              url: extraConditionsUrl,
              params: {
                company: quotationData?.Company,
                docNumber: quotationData?.DocNumber,
                filename: String(filename || "").trim() || undefined,
                langId: language,
              },
              hasAuthHeader: !!headers?.Authorization,
              message: QuotationPdfService.formatError(e, "extraConditions"),
              status: e?.response?.status,
              response: e?.response?.data,
            });
          } catch (_) {}
          return [];
        }
      };

      const fetchTransactionItems = async () => {
        try {
          const params = {
            company: quotationData?.Company,
            docNumber: quotationData?.DocNumber,
            language,
          };
          if (revision !== null && revision !== undefined) params.revision = revision;

          const res = await axios.get(transactionItemUrl, { params, headers, timeout: timeoutMs });
          const payload = res?.data?.data ?? res?.data ?? null;
          if (payload?.success === false) return [];

          const rows =
            (Array.isArray(payload) && payload) ||
            payload?.data ||
            payload?.rows ||
            payload?.QuotationTransactionItem ||
            payload?.QuotationTransactionItems ||
            [];

          return normalizeArray(rows);
        } catch (e) {
          try {
            // eslint-disable-next-line no-console
            console.warn("[QuotationPdfService] transactionItem request failed", {
              url: transactionItemUrl,
              params: {
                company: quotationData?.Company,
                docNumber: quotationData?.DocNumber,
                language,
              },
              hasAuthHeader: !!headers?.Authorization,
              message: QuotationPdfService.formatError(e, "transactionItem"),
              status: e?.response?.status,
            });
          } catch (_) {}
          return [];
        }
      };

      let docHeaderImageFilename = String(docHeaderImageFilenameFromCaller || "").trim();
      let docHeaderImageFileLocation = String(docHeaderImageFileLocationFromCaller || "").trim();
      if (!docHeaderImageFilename && docHeaderTypeCode && quotationData?.Company) {
        try {
          const url = `${process.env.REACT_APP_API_URL}quotationTransaction/docHeaderImage`;
          const normalizeToArray = (val, visited = new WeakSet()) => {
            if (Array.isArray(val)) return val;
            if (!val || typeof val !== "object") return [];
            if (visited.has(val)) return [];
            visited.add(val);

            for (const k of Object.keys(val)) {
              const child = val[k];
              if (Array.isArray(child)) return child;
            }
            for (const k of Object.keys(val)) {
              const child = val[k];
              if (child && typeof child === "object") {
                const arr = normalizeToArray(child, visited);
                if (arr.length) return arr;
              }
            }
            return [];
          };

          const resolveFromResponse = (res) => {
            const raw = res?.data?.data ?? res?.data ?? null;
            const rows = normalizeToArray(raw);
            const obj = rows.length ? rows[0] : (Array.isArray(raw) ? raw[0] : raw);
            const filename = String(
              obj?.ImageFilename ??
                obj?.imageFilename ??
                obj?.ImageFileName ??
                obj?.imageFileName ??
                ""
            ).trim();
            const location = String(obj?.FileLocation ?? obj?.fileLocation ?? "").trim();
            return { filename, location, raw: res?.data };
          };

          const res = await axios.get(url, {
            params: {
              company: quotationData.Company,
              docHeaderType: docHeaderTypeCode,
              lookupcode: docHeaderTypeCode,
              lookupCode: docHeaderTypeCode,
            },
            headers,
            timeout: timeoutMs,
          });

          const resolved = resolveFromResponse(res);
          docHeaderImageFilename = resolved.filename;
          docHeaderImageFileLocation = resolved.location;

          if (!docHeaderImageFilename) {
            try {
              // eslint-disable-next-line no-console
              console.warn("[QuotationPdfService] docHeaderImage returned empty mapping (downloadPdf)", {
                url,
                params: {
                  company: quotationData.Company,
                  docHeaderType: docHeaderTypeCode,
                  lookupcode: docHeaderTypeCode,
                },
                hasAuthHeader: !!headers?.Authorization,
                response: resolved.raw,
              });
            } catch (_) {}
          }
        } catch (e) {
          try {
            // eslint-disable-next-line no-console
            console.warn("[QuotationPdfService] docHeaderImage request failed (downloadPdf)", {
              company: quotationData?.Company,
              docHeaderType: docHeaderTypeCode,
              hasAuthHeader: !!headers?.Authorization,
              message: QuotationPdfService.formatError(e, "docHeaderImage"),
            });
          } catch (_) {}
        }
      }

      let apiData = null;
      try {
        const params = {
          docNumber: quotationData.DocNumber,
          company: quotationData.Company,
          language,
        };
        if (salesmanCode) params.salesmanCode = salesmanCode;
        if (revision !== null && revision !== undefined) params.revision = revision;

        const response = await axios.get(withDetailUrl, {
          params,
          headers,
          timeout: timeoutMs,
        });
        apiData = response.data?.data || null;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404 || status === 401 || status === 403) {
          try {
            const response = await axios.get(findByIdUrl, {
              params: {
                docNumber: quotationData.DocNumber,
                company: quotationData.Company,
                language,
                ...(revision !== null && revision !== undefined ? { revision } : {}),
              },
              headers,
              timeout: timeoutMs,
            });
            apiData = response.data?.data || response.data;
          } catch (err2) {
            if (QuotationPdfService.isDbConnectivityError(err) || QuotationPdfService.isDbConnectivityError(err2)) {
              throw new Error(QuotationPdfService.dbConnectivityErrorMessage());
            }
            const ctx = status === 404
              ? "โหลดข้อมูลเอกสาร (withDetail ไม่พบ)"
              : "โหลดข้อมูลเอกสาร (withDetail ไม่มีสิทธิ์)";
            throw new Error(
              `${QuotationPdfService.formatError(err, ctx)} | fallback(findById): ${QuotationPdfService.formatError(err2)}`
            );
          }
        } else {
          throw new Error(QuotationPdfService.formatError(err, "โหลดข้อมูลเอกสาร (withDetail) ไม่สำเร็จ"));
        }
      }

      if (!apiData) {
        throw new Error("ไม่พบข้อมูลเอกสารสำหรับดาวน์โหลด PDF");
      }

      const footerFromApi =
        apiData.ProductRemarksFooter ??
        apiData.productRemarksFooter ??
        apiData.ProductRemarkFooter ??
        apiData.productRemarkFooter ??
        apiData.ProductRemarksFooters ??
        apiData.productRemarksFooters;

      const remarksFromApi =
        apiData.ProductRemarks ??
        apiData.productRemarks ??
        apiData.ProductRemark ??
        apiData.productRemark;

      const footerSourceKey =
        apiData.ProductRemarksFooter !== undefined ? "ProductRemarksFooter" :
        apiData.productRemarksFooter !== undefined ? "productRemarksFooter" :
        apiData.ProductRemarkFooter !== undefined ? "ProductRemarkFooter" :
        apiData.productRemarkFooter !== undefined ? "productRemarkFooter" :
        apiData.ProductRemarksFooters !== undefined ? "ProductRemarksFooters" :
        apiData.productRemarksFooters !== undefined ? "productRemarksFooters" :
        null;

      const remarksSourceKey =
        apiData.ProductRemarks !== undefined ? "ProductRemarks" :
        apiData.productRemarks !== undefined ? "productRemarks" :
        apiData.ProductRemark !== undefined ? "ProductRemark" :
        apiData.productRemark !== undefined ? "productRemark" :
        null;

      const normalizedFooterFromApi = normalizeArray(footerFromApi);
      const normalizedRemarksFromApi = normalizeArray(remarksFromApi);

      const hasFooterRemarks = normalizedFooterFromApi.length > 0;
      const hasRemarks = normalizedRemarksFromApi.length > 0;

      try {
        const sample = normalizedFooterFromApi.slice(0, 8).map((r) => {
          const keys = r && typeof r === "object" ? Object.keys(r).slice(0, 25) : [];
          const productCode = r?.ProductCode ?? r?.productCode ?? r?.ItemCode ?? r?.itemCode;
          const lineNo = r?.LineNo ?? r?.lineNo ?? r?.Seq ?? r?.seq ?? r?.Line ?? r?.line;
          const subject = r?.Subject ?? r?.subject ?? r?.Title ?? r?.title;
          const remarks = r?.Remarks ?? r?.remarks ?? r?.Remark ?? r?.remark ?? r?.Description ?? r?.description;
          const remarksText = String(remarks ?? "");
          return {
            keys,
            productCode,
            lineNo,
            subject,
            remarksLen: remarksText.length,
            remarksPreview: remarksText.slice(0, 160),
          };
        });

        // eslint-disable-next-line no-console
        console.log("[QuotationPdfService] ProductRemarksFooter payload (downloadPdf)", {
          DocNumber: quotationData?.DocNumber,
          Company: quotationData?.Company,
          footerSourceKey,
          remarksSourceKey,
          footerRawType: footerFromApi === null ? "null" : Array.isArray(footerFromApi) ? "array" : typeof footerFromApi,
          footerNormalizedCount: normalizedFooterFromApi.length,
          remarksNormalizedCount: normalizedRemarksFromApi.length,
          footerSample: sample,
        });
      } catch (_) {}

      apiData.ProductRemarksFooter = normalizedFooterFromApi;
      apiData.ProductRemarks = normalizedRemarksFromApi;

      if (!hasFooterRemarks || !hasRemarks) {
        try {
          const response2 = await axios.get(findByIdUrl, {
            params: {
              docNumber: quotationData.DocNumber,
              company: quotationData.Company,
              language,
            },
            headers,
            timeout: timeoutMs,
          });
          const fallbackData = response2.data?.data || response2.data;

          if (fallbackData) {
            const footerFromFallback =
              fallbackData.ProductRemarksFooter ??
              fallbackData.productRemarksFooter ??
              fallbackData.ProductRemarkFooter ??
              fallbackData.productRemarkFooter ??
              fallbackData.ProductRemarksFooters ??
              fallbackData.productRemarksFooters;

            const remarksFromFallback =
              fallbackData.ProductRemarks ??
              fallbackData.productRemarks ??
              fallbackData.ProductRemark ??
              fallbackData.productRemark;

            if (!hasFooterRemarks) {
              apiData.ProductRemarksFooter = normalizeArray(footerFromFallback);
            }
            if (!hasRemarks) {
              apiData.ProductRemarks = normalizeArray(remarksFromFallback);
            }
          }
        } catch (_) {
          // ignore fallback failure
        }
      }

      apiData.ProductRemarksFooter = normalizeArray(apiData.ProductRemarksFooter);
      apiData.ProductRemarks = normalizeArray(apiData.ProductRemarks);

      try {
        // eslint-disable-next-line no-console
        console.log("[QuotationPdfService] ProductRemarksFooter normalized (final, downloadPdf)", {
          DocNumber: quotationData?.DocNumber,
          Company: quotationData?.Company,
          count: apiData.ProductRemarksFooter.length,
          sample: apiData.ProductRemarksFooter.slice(0, 8),
        });
      } catch (_) {}

      const headerCandidate = apiData.QuotationTransaction || apiData;
      const headerObj = Array.isArray(headerCandidate) ? headerCandidate[0] : headerCandidate;
      const header =
        headerObj && typeof headerObj === "object"
          ? { ...headerObj }
          : {};

      const qtFilename = String(
        quotationData?.Filename ??
          quotationData?.filename ??
          header?.Filename ??
          header?.filename ??
          apiData?.Filename ??
          apiData?.filename ??
          ""
      ).trim();

      const extraConditions = qtFilename
        ? await fetchExtraConditions({ filename: qtFilename })
        : [];
      if (!qtFilename) {
        try {
          // eslint-disable-next-line no-console
          console.warn("[QuotationPdfService] extraConditions skipped (downloadPdf): missing QuotationTransaction.Filename", {
            DocNumber: quotationData?.DocNumber,
            Company: quotationData?.Company,
            headerFilename: header?.Filename,
          });
        } catch (_) {}
      }

      const copyIfMissing = (key) => {
        if (header[key] !== undefined && header[key] !== null && header[key] !== "") return;
        const v = apiData?.[key];
        if (v === undefined || v === null || v === "") return;
        header[key] = v;
      };
      [
        "Total_Sum",
        "Total_sum",
        "Total_Vat",
        "Total_vat",
        "Total_GrandTotal",
        "Total_Grandtotal",
        "Total_Total",
        "Total_Discount",
      ].forEach(copyIfMissing);

      const copyIfMissingFromInput = (key) => {
        if (header[key] !== undefined && header[key] !== null && header[key] !== "") return;
        const v = quotationData?.[key];
        if (v === undefined || v === null || v === "") return;
        header[key] = v;
      };
      [
        "QtStatusName",
        "DocStatusName",
        "DocStatus",
      ].forEach(copyIfMissingFromInput);
      const customer = apiData.Customer || apiData.customer || null;
      const contact = apiData.Contact || apiData.contact || null;

      // Ensure generator receives normalized arrays
      apiData.ProductRemarks = normalizeArray(apiData.ProductRemarks);
      apiData.ProductRemarksFooter = normalizeArray(apiData.ProductRemarksFooter);

      // Fetch transaction items from the new endpoint (preferred) to get Unit1/Quantity1/Unit2/Quantity2.
      // Fall back to the existing withDetail/findById payload if the new endpoint returns empty.
      const transactionItemsFromApi = await fetchTransactionItems();

      const documentData = {
        DocNumber: header?.DocNumber || quotationData.DocNumber,
        Company: header?.Company || quotationData.Company,
        QuotationTransaction: header,
        Customer: customer,
        Contact: contact,
        QuotationTransactionItem:
          transactionItemsFromApi.length
            ? transactionItemsFromApi
            : apiData.QuotationTransactionItem || headerCandidate?.QuotationTransactionItem || [],
        QuotationTransactionDiscount:
          apiData.QuotationTransactionDiscount || apiData.OrderTransactionDiscount || [],
        ProductRemarks: apiData.ProductRemarks || [],
        ProductRemarksFooter: apiData.ProductRemarksFooter || [],
        ExtraConditions: extraConditions,
        TransactionStatus: apiData.TransactionStatus || null,
      };

      try {
        // eslint-disable-next-line no-console
        console.log("[QuotationPdfService] documentData for PDF", documentData);
      } catch (_) {}

      if (pdfType === 'without_date') {
        if (documentData.QuotationTransaction) {
          documentData.QuotationTransaction.DocDate = null;
          documentData.QuotationTransaction.DueDate = null;
          documentData.QuotationTransaction.ValidDate = null;
        }
      }

      await fontReadyPromise;

      // Strict company header/footer from ENV only.
      const strictCompanyHeaderFooter = true;
      const company = String(documentData?.Company || "").trim();
      const fileBaseUrl = getPhotoDocumentBaseUrl();
      const location = "";
      const baseEndsWithLocation = (baseUrl, loc) => {
        const base = String(baseUrl || "").trim().replace(/\/+$/, "");
        const locationText = String(loc || "").trim().replace(/^\/+|\/+$/g, "");
        if (!base || !locationText) return false;
        return base.toLowerCase().endsWith(`/${locationText.toLowerCase()}`);
      };

      const hasDynamicHeaderSelection = !!(
        String(docHeaderTypeCode || "").trim() ||
        String(docHeaderImageFilename || "").trim() ||
        String(docHeaderImageFileLocation || "").trim()
      );

      const headerUrlSelected = (() => {
        const fn = String(docHeaderImageFilename || "").trim();
        if (!fn) return "";
        const loc = String(docHeaderImageFileLocation || "").trim();
        const effectiveLoc = baseEndsWithLocation(fileBaseUrl, loc) ? "" : loc;
        return joinUrl(fileBaseUrl, effectiveLoc, fn);
      })();

      const headerUrlCompany = company
        ? joinUrl(fileBaseUrl, location, `${company}_header_quotation.png`)
        : "";

      const headerUrl = (hasDynamicHeaderSelection && headerUrlSelected) ? headerUrlSelected : headerUrlCompany;
      const footerUrl = company
        ? joinUrl(fileBaseUrl, location, `${company}_footer_quotation.png`)
        : "";

      let allowMissingCompanyHeaderFooter = false;
      const missing = [];
      let headerStatus = { url: preferHttpsIfPageIsHttps(headerUrl), exists: false, usable: false };
      let footerStatus = { url: preferHttpsIfPageIsHttps(footerUrl), exists: false, usable: false };
      if (!fileBaseUrl || !company) {
        missing.push("header", "footer");
      } else {
        const [h, f] = await Promise.all([
          checkImageLoadability(headerUrl, { timeoutMs }),
          checkImageLoadability(footerUrl, { timeoutMs }),
        ]);
        headerStatus = h;
        footerStatus = f;
        if (!h.usable) missing.push("header");
        if (!f.usable) missing.push("footer");
      }

      if (missing.length) {
        const proceed = await confirmProceedWithoutHeaderFooter({
          company,
          missing,
          urls: { header: headerStatus.url, footer: footerStatus.url },
          statuses: { header: headerStatus, footer: footerStatus },
          translate,
          translator,
        });
        if (!proceed) {
          throw new Error("ยกเลิกการเปิด PDF เนื่องจากไม่พบ Header/Footer ของบริษัท");
        }
        allowMissingCompanyHeaderFooter = true;
      }

      const generator = new QuotationPdfGenerator(documentData, {
        summaryOnEveryPage: true,
        docHeaderTypeCode,
        docHeaderImageFilename,
        docHeaderImageFileLocation,
        strictCompanyHeaderFooter,
        allowMissingCompanyHeaderFooter,
        companyHeaderFooterLocation: location,
        debugTotals: true,
        translate,
        translator,
      });

      let pdf = null;
      try {
        pdf = await generator.generate();
      } catch (e) {
        const msg = String(e?.message || "");
        const isHeaderErr = msg.includes("PDF_HEADER_NOT_FOUND_OR_UNUSABLE");
        const isFooterErr = msg.includes("PDF_FOOTER_NOT_FOUND_OR_UNUSABLE");

        if (strictCompanyHeaderFooter && !allowMissingCompanyHeaderFooter && (isHeaderErr || isFooterErr)) {
          const missing2 = [];
          if (isHeaderErr) missing2.push("header");
          if (isFooterErr) missing2.push("footer");

          const proceed = await confirmProceedWithoutHeaderFooter({
            company,
            missing: missing2.length ? missing2 : ["header", "footer"],
            urls: { header: headerStatus.url, footer: footerStatus.url },
            statuses: { header: headerStatus, footer: footerStatus },
            translate,
            translator,
          });
          if (!proceed) throw e;

          const generator2 = new QuotationPdfGenerator(documentData, {
            summaryOnEveryPage: true,
            docHeaderTypeCode,
            docHeaderImageFilename,
            docHeaderImageFileLocation,
            strictCompanyHeaderFooter,
            allowMissingCompanyHeaderFooter: true,
            companyHeaderFooterLocation: location,
            debugTotals: true,
            translate,
            translator,
          });
          pdf = await generator2.generate();
        } else {
          throw e;
        }
      }
      
      // ดาวน์โหลด PDF
      pdf.save(`Quotation-${quotationData.DocNumber}.pdf`);

      return { success: true };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[QuotationPdfService] downloadPdf failed", error);
      const msg = QuotationPdfService.formatError(error, "ดาวน์โหลด PDF ไม่สำเร็จ");
      throw new Error(msg);
    }
  }
}
